import type {
  AdminCompanyBusinessTypeMetric,
  AdminPaymentBusinessTypeMetric,
  AdminSubscriptionBusinessTypeMetric,
} from "../../Graphql/types/adminAnalytics";
import {
  currencyForAdminCountry,
  labelForAdminCountry,
} from "../common/adminMarketCountries";

export interface DashboardRow {
  businessType: string;
  companiesTotal: number;
  companiesActive: number;
  companiesPending: number;
  subscriptionsTotal: number;
  subscriptionsActive: number;
  revenueByCurrency: { currency: string; amount: number }[];
  completedPayments: number;
  byCountry: DashboardCountryRow[];
}

export interface DashboardCountryRow {
  countryCode: string;
  companiesTotal: number;
  companiesActive: number;
  subscriptionsTotal: number;
  subscriptionsActive: number;
  revenueByCurrency: { currency: string; amount: number }[];
  completedPayments: number;
}

export interface DashboardSummary {
  companiesTotal: number;
  companiesActive: number;
  subscriptionsTotal: number;
  subscriptionsActive: number;
  revenueByCurrency: { currency: string; amount: number }[];
  completedPayments: number;
}

function mergeCurrencyAmounts(
  target: Map<string, number>,
  currency: string,
  amount: number,
) {
  target.set(currency, (target.get(currency) ?? 0) + amount);
}

function mapToCurrencyList(map: Map<string, number>) {
  return [...map.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

function revenueFromPaymentCountries(
  byCountry:
    | { countryCode: string; totalRevenue?: number | null }[]
    | null
    | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of byCountry ?? []) {
    const amount = row.totalRevenue ?? 0;
    if (amount <= 0) continue;
    mergeCurrencyAmounts(map, currencyForAdminCountry(row.countryCode), amount);
  }
  return map;
}

export function buildDashboardRows(
  companies?: AdminCompanyBusinessTypeMetric[] | null,
  payments?: AdminPaymentBusinessTypeMetric[] | null,
  subscriptions?: AdminSubscriptionBusinessTypeMetric[] | null,
  countryCode?: string,
  businessType?: string,
): DashboardRow[] {
  const types = new Set<string>([
    ...(companies ?? []).map((c) => c.businessType),
    ...(payments ?? []).map((p) => p.businessType),
    ...(subscriptions ?? []).map((s) => s.businessType),
  ]);

  let typeList = [...types].sort();
  if (businessType) {
    typeList = typeList.filter((t) => t === businessType);
  }

  return typeList.map((bt) => {
    const company = (companies ?? []).find((c) => c.businessType === bt);
    const payment = (payments ?? []).find((p) => p.businessType === bt);
    const subscription = (subscriptions ?? []).find(
      (s) => s.businessType === bt,
    );

    const countryCodes = new Set<string>();
    for (const row of company?.byCountry ?? [])
      countryCodes.add(row.countryCode);
    for (const row of payment?.byCountry ?? [])
      countryCodes.add(row.countryCode);
    for (const row of subscription?.byCountry ?? [])
      countryCodes.add(row.countryCode);

    let countryList = [...countryCodes].sort();
    if (countryCode) {
      countryList = countryList.filter((c) => c === countryCode);
    }

    const byCountry: DashboardCountryRow[] = countryList.map((code) => {
      const cRow = company?.byCountry?.find((c) => c.countryCode === code);
      const pRow = payment?.byCountry?.find((c) => c.countryCode === code);
      const sRow = subscription?.byCountry?.find((c) => c.countryCode === code);
      const rev = new Map<string, number>();
      if (pRow?.totalRevenue) {
        mergeCurrencyAmounts(
          rev,
          currencyForAdminCountry(code),
          pRow.totalRevenue,
        );
      }
      return {
        countryCode: code,
        companiesTotal: cRow?.total ?? 0,
        companiesActive: cRow?.active ?? 0,
        subscriptionsTotal: sRow?.total ?? 0,
        subscriptionsActive: sRow?.active ?? 0,
        revenueByCurrency: mapToCurrencyList(rev),
        completedPayments: pRow?.completedPayments ?? 0,
      };
    });

    const useCountrySlice = Boolean(countryCode);
    const revenueMap = useCountrySlice
      ? byCountry.reduce((m, row) => {
          for (const r of row.revenueByCurrency) {
            mergeCurrencyAmounts(m, r.currency, r.amount);
          }
          return m;
        }, new Map<string, number>())
      : revenueFromPaymentCountries(payment?.byCountry);

    return {
      businessType: bt,
      companiesTotal: useCountrySlice
        ? byCountry.reduce((n, r) => n + r.companiesTotal, 0)
        : (company?.total ?? 0),
      companiesActive: useCountrySlice
        ? byCountry.reduce((n, r) => n + r.companiesActive, 0)
        : (company?.active ?? 0),
      companiesPending: useCountrySlice
        ? (company?.byCountry?.find((c) => c.countryCode === countryCode)
            ?.pending ?? 0)
        : (company?.pending ?? 0),
      subscriptionsTotal: useCountrySlice
        ? byCountry.reduce((n, r) => n + r.subscriptionsTotal, 0)
        : (subscription?.total ?? 0),
      subscriptionsActive: useCountrySlice
        ? byCountry.reduce((n, r) => n + r.subscriptionsActive, 0)
        : (subscription?.active ?? 0),
      revenueByCurrency: mapToCurrencyList(revenueMap),
      completedPayments: useCountrySlice
        ? byCountry.reduce((n, r) => n + r.completedPayments, 0)
        : (payment?.completedPayments ?? 0),
      byCountry,
    };
  });
}

export function summarizeDashboardRows(rows: DashboardRow[]): DashboardSummary {
  const revenueMap = new Map<string, number>();
  let companiesTotal = 0;
  let companiesActive = 0;
  let subscriptionsTotal = 0;
  let subscriptionsActive = 0;
  let completedPayments = 0;

  for (const row of rows) {
    companiesTotal += row.companiesTotal;
    companiesActive += row.companiesActive;
    subscriptionsTotal += row.subscriptionsTotal;
    subscriptionsActive += row.subscriptionsActive;
    completedPayments += row.completedPayments;
    for (const r of row.revenueByCurrency) {
      mergeCurrencyAmounts(revenueMap, r.currency, r.amount);
    }
  }

  return {
    companiesTotal,
    companiesActive,
    subscriptionsTotal,
    subscriptionsActive,
    revenueByCurrency: mapToCurrencyList(revenueMap),
    completedPayments,
  };
}

export function hasBreakdownData(
  companies?: AdminCompanyBusinessTypeMetric[] | null,
  payments?: AdminPaymentBusinessTypeMetric[] | null,
  subscriptions?: AdminSubscriptionBusinessTypeMetric[] | null,
): boolean {
  return (
    (companies?.length ?? 0) > 0 ||
    (payments?.length ?? 0) > 0 ||
    (subscriptions?.length ?? 0) > 0
  );
}

/** Flatten country rows across industries for a dedicated country table */
export function aggregateCountriesFromRows(
  rows: DashboardRow[],
): DashboardCountryRow[] {
  const map = new Map<string, DashboardCountryRow>();

  for (const row of rows) {
    for (const c of row.byCountry) {
      const existing = map.get(c.countryCode);
      if (!existing) {
        map.set(c.countryCode, {
          ...c,
          revenueByCurrency: [...c.revenueByCurrency],
        });
        continue;
      }
      existing.companiesTotal += c.companiesTotal;
      existing.companiesActive += c.companiesActive;
      existing.subscriptionsTotal += c.subscriptionsTotal;
      existing.subscriptionsActive += c.subscriptionsActive;
      existing.completedPayments += c.completedPayments;
      const revMap = new Map(
        existing.revenueByCurrency.map((r) => [r.currency, r.amount]),
      );
      for (const r of c.revenueByCurrency) {
        revMap.set(r.currency, (revMap.get(r.currency) ?? 0) + r.amount);
      }
      existing.revenueByCurrency = mapToCurrencyList(revMap);
    }
  }

  return [...map.values()].sort((a, b) =>
    labelForAdminCountry(a.countryCode).localeCompare(
      labelForAdminCountry(b.countryCode),
    ),
  );
}
